/*
 * Acoustic Advice Form -> Gorgias Create Ticket
 * ---------------------------------------------
 * Gathers the form fields and builds a payload for the Gorgias Create Ticket
 * API (https://developers.gorgias.com/reference/create-ticket).
 *
 * IMPORTANT: the Gorgias API requires a private API key (Basic auth) and MUST
 * NOT be called directly from the browser. This posts a normalized payload to a
 * server proxy (set via the section's "Submit endpoint" setting) which injects
 * the credentials and forwards to Gorgias.
 *
 * Field strategy (per request):
 *   - [data-custom-field]  -> Gorgias custom fields (select/radio choice values,
 *                             multi-select checkboxes joined ", ", and free text;
 *                             the values Gorgias can run analytics on). These are
 *                             ALSO composed into the body.
 *   - [data-body-field]    -> composed into the ticket message body only
 */
(function () {
  'use strict';

  // Gorgias upload limits (https://developers.gorgias.com/reference/post_api-upload).
  var MAX_FILES = 10;
  var MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

  // Customers share many measurement/export file types, so all types are allowed
  // EXCEPT executables/scripts. This is a client-side denylist (a convenience +
  // first line of defense); the server MUST also enforce it — client checks can
  // be bypassed. Match by lowercased file extension.
  var BLOCKED_EXTENSIONS = [
    'exe', 'msi', 'msix', 'com', 'scr', 'pif', 'cpl', 'bat', 'cmd',
    'ps1', 'psm1', 'ps1xml', 'vbs', 'vbe', 'wsf', 'wsh', 'hta', 'jse', 'js',
    'jar', 'app', 'dmg', 'pkg', 'deb', 'rpm', 'sh', 'bash', 'zsh', 'run',
    'apk', 'ipa', 'gadget', 'msc', 'reg', 'scf', 'lnk', 'inf', 'dll', 'sys',
    'command', 'action', 'workflow'
  ];

  // ISO 3166-1 alpha-2 codes. The country <select> is rebuilt from these so its
  // option VALUES are stable ISO codes (Shopify's all_country_option_tags only
  // gives localized names). The ISO is used SOLELY to route the ticket to the
  // right agent region server-side — it is never sent as ticket content; the
  // localized country NAME (the option text) still flows into the ticket body.
  var COUNTRY_ISO = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE ' +
    'BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL ' +
    'CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK ' +
    'FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR ' +
    'HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY ' +
    'KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ ' +
    'MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG ' +
    'PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK ' +
    'SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW ' +
    'TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' ');

  // Rebuild the country <select> with ISO-coded option values + localized display
  // names (Intl.DisplayNames, storefront locale). Leaves the server-rendered
  // options untouched when Intl.DisplayNames is unavailable (very old browsers) —
  // the form still works; routing just falls back to the submitting shop.
  function populateCountryIso(select, locale) {
    if (!select) return;
    var DisplayNames = window.Intl && Intl.DisplayNames;
    if (!DisplayNames) return;
    var namer;
    try {
      namer = new DisplayNames([locale || 'en', 'en'], { type: 'region' });
    } catch (e) {
      try { namer = new DisplayNames(['en'], { type: 'region' }); }
      catch (e2) { return; }
    }
    var items = [];
    COUNTRY_ISO.forEach(function (iso) {
      var name;
      try { name = namer.of(iso); } catch (e) { name = ''; }
      if (name && name !== iso) items.push({ iso: iso, name: name });
    });
    if (!items.length) return; // nothing resolved — keep the SSR options
    items.sort(function (a, b) { return a.name.localeCompare(b.name, locale || 'en'); });
    var placeholder = select.querySelector('option[value=""]');
    select.innerHTML = '';
    if (placeholder) select.appendChild(placeholder);
    items.forEach(function (it) {
      var opt = document.createElement('option');
      opt.value = it.iso;
      opt.textContent = it.name;
      select.appendChild(opt);
    });
  }

  function fileExtension(name) {
    var i = name.lastIndexOf('.');
    return i > -1 ? name.slice(i + 1).toLowerCase() : '';
  }

  function isBlockedFile(name) {
    return BLOCKED_EXTENSIONS.indexOf(fileExtension(name)) > -1;
  }

  // Returns an error message string if the selection is invalid, else ''.
  function validateFiles(files, messages) {
    if (files.length > MAX_FILES) {
      return (messages.fileTooMany || 'You can attach up to {n} files.').replace('{n}', MAX_FILES);
    }
    for (var i = 0; i < files.length; i++) {
      if (isBlockedFile(files[i].name)) {
        return (messages.fileBlocked || "This file type can't be uploaded: {name}").replace('{name}', files[i].name);
      }
      if (files[i].size > MAX_FILE_BYTES) {
        return (messages.fileTooLarge || 'Each file must be under {n} MB.').replace('{n}', MAX_FILE_BYTES / 1024 / 1024)
          + ' (' + files[i].name + ')';
      }
    }
    return '';
  }

  function collect(root) {
    var form = root.querySelector('[data-acoustic-advice-form]');
    if (!form) return null;

    var endpoint = root.dataset.endpoint || '';
    var shop = root.dataset.shop || '';
    var messages = {
      submitting: root.dataset.msgSubmitting || 'Sending…',
      success: root.dataset.msgSuccess || 'Thank you! Your request has been received.',
      error: root.dataset.msgError || 'Something went wrong. Please try again or email us directly.',
      fileTooMany: root.dataset.msgFileTooMany || '',
      fileTooLarge: root.dataset.msgFileTooLarge || '',
      fileBlocked: root.dataset.msgFileBlocked || '',
      removeFile: root.dataset.msgRemoveFile || 'Remove'
    };
    return { form: form, endpoint: endpoint, shop: shop, messages: messages };
  }

  // Decode HTML entities to plain text. `data-*` label attributes are rendered
  // through Liquid `| escape`, so a value like "room's" arrives as "room&#39;s"
  // (and can be escaped more than once). Decode repeatedly until stable so the
  // server always receives clean text — otherwise entities surface in Gorgias.
  var _decoder;
  function decodeEntities(str) {
    if (!str || str.indexOf('&') === -1) return str;
    _decoder = _decoder || document.createElement('textarea');
    var prev;
    do {
      prev = str;
      _decoder.innerHTML = str;
      str = _decoder.value;
    } while (str !== prev && str.indexOf('&') !== -1);
    return str;
  }

  // Returns the human-readable value of a control (selected option text for
  // selects, checked-radio label text, otherwise the raw value).
  function displayValue(el) {
    if (el.tagName === 'SELECT') {
      var opt = el.options[el.selectedIndex];
      return opt ? opt.textContent.trim() : '';
    }
    if (el.type === 'radio') {
      var checked = el.form.querySelector('input[name="' + el.name + '"]:checked');
      if (!checked) return '';
      var label = checked.closest('label');
      var titleEl = label && label.querySelector('.aaf__radio-title');
      return (titleEl ? titleEl.textContent : (label ? label.textContent : checked.value)).trim();
    }
    if (el.type === 'checkbox') {
      // Checkbox group: join the display labels of every checked box in the group.
      var boxes = el.form.querySelectorAll('input[type="checkbox"][name="' + el.name + '"]:checked');
      var labels = [];
      Array.prototype.forEach.call(boxes, function (box) {
        var lbl = box.closest('label');
        var t = lbl && lbl.querySelector('.aaf__radio-title');
        labels.push((t ? t.textContent : (lbl ? lbl.textContent : box.value)).trim());
      });
      return labels.join(', ');
    }
    return el.value.trim();
  }

  function buildPayload(ctx) {
    var form = ctx.form;
    var email = '';
    var firstName = '';
    var lastName = '';
    // customFields = analytics/option fields (server maps name -> Gorgias custom-field id).
    // fields       = every non-option field, for the customer object / reference.
    // answers      = every field in FORM order (label + display value) — the server
    //               composes the ticket message body from this so the rep sees the
    //               full submission exactly as the customer filled it out.
    var customFields = {};
    var fields = {};
    var answers = [];
    var seenRadios = {};
    var seenCheckboxes = {};

    var controls = form.querySelectorAll('[data-body-field], [data-custom-field]');

    controls.forEach(function (el) {
      if (el.name === 'first_name') firstName = el.value.trim();
      if (el.name === 'last_name') lastName = el.value.trim();
      if (el.hasAttribute('data-customer-email')) email = el.value.trim();

      // radios: process the group once, using the checked value
      if (el.type === 'radio') {
        if (seenRadios[el.name]) return;
        seenRadios[el.name] = true;
      }

      // checkboxes: process the group once; displayValue joins all checked labels
      if (el.type === 'checkbox') {
        if (seenCheckboxes[el.name]) return;
        seenCheckboxes[el.name] = true;
      }

      var label = decodeEntities(el.getAttribute('data-body-label') || el.name);
      var text = displayValue(el);
      if (!text) return;

      // Ordered, human-readable record of the answer (localized display text).
      answers.push({ name: el.name, label: label, value: text });

      if (el.hasAttribute('data-custom-field')) {
        // value = canonical (language-independent) option value(s) for consistent
        // analytics grouping across locales; text = localized display label.
        var rawValue;
        if (el.type === 'radio') {
          rawValue = (form.querySelector('input[name="' + el.name + '"]:checked') || {}).value || '';
        } else if (el.type === 'checkbox') {
          // Multi-select group: join the canonical value of every checked box,
          // comma-separated (the group is processed once, on the first box).
          var checkedBoxes = form.querySelectorAll('input[type="checkbox"][name="' + el.name + '"]:checked');
          rawValue = Array.prototype.map.call(checkedBoxes, function (b) { return b.value; }).join(', ');
        } else {
          rawValue = el.value;
        }
        customFields[el.name] = { value: rawValue, text: text, label: label };
      } else {
        fields[el.name] = { value: text, label: label };
      }
    });

    var name = (firstName + ' ' + lastName).trim();

    return {
      subject: 'Acoustic Advice Request' + (name ? ' — ' + name : ''),
      customer: { email: email, name: name },
      customFields: customFields,
      fields: fields,
      answers: answers,
      meta: {
        locale: document.documentElement.lang || '',
        sourceUrl: window.location.href,
        shop: ctx.shop,
        // Selected country's ISO code — used ONLY to route the ticket to the right
        // agent region on the server. Never rendered into the ticket; the country
        // NAME still reaches the body via the `country` body field above. Blank
        // when the select still holds server-rendered (non-ISO) option values.
        countryCode: (function () {
          var el = form.querySelector('select[name="country"]');
          var v = el ? el.value.trim() : '';
          return /^[A-Za-z]{2}$/.test(v) ? v.toUpperCase() : '';
        })()
      }
    };
  }

  // Fire a Triple Whale custom event for paid-media attribution reporting.
  // Docs: https://kb.triplewhale.com/en/articles/9957947-tracking-custom-events-with-triple-pixel-attribution
  // The Triple Pixel is loaded storefront-wide via the Triple Whale app embed,
  // so window.TriplePixel is normally present (and stubs a queue before it fully
  // loads). Fully guarded so a blocked/absent pixel can never break the form.
  // Only the event name is required; every property is optional — we pass the
  // customer email (for identity stitching) plus the canonical option-field
  // values so the event is filterable in Triple Whale.
  function trackTripleWhale(payload) {
    try {
      if (typeof window.TriplePixel !== 'function') return;
      var email = (payload.customer && payload.customer.email) || '';

      // Identity stitching: associate this visitor with their email so Triple
      // Whale can attribute the lead to a customer profile. This is what
      // populates the event's top-level `email` column — custom-event props
      // only ever land inside the event `data`, never the reserved columns.
      if (email) window.TriplePixel('contact', { email: email });

      var props = { email: email };
      // store (which shop) + country as event properties, so they're queryable
      // inside the event `data`. NOTE: these do NOT fill Triple Whale's reserved
      // top-level `store` / `country` columns — those are set automatically by
      // the pixel (shop) and by geo/IP.
      var shop = payload.meta && payload.meta.shop;
      if (shop) props.store = shop;
      var country =
        payload.fields && payload.fields.country && payload.fields.country.value;
      if (country) props.country = country;

      var cf = payload.customFields || {};
      Object.keys(cf).forEach(function (name) {
        var value = cf[name] && cf[name].value;
        if (value != null && value !== '') props[name] = value;
      });
      window.TriplePixel('custom', 'advice_form_submitted', props);
    } catch (err) {
      // Analytics must never interrupt the submit flow.
      if (window.console) console.warn('[acoustic-advice-form] TriplePixel event failed', err);
    }
  }

  // Push a GTM dataLayer event on a successful submit. The web GTM container
  // listens for the `hs_lead` Custom Event and forwards it to GA4; server-side
  // GTM's GA4 client then fires the "Google Ads - Conv - Free Consultation" tag
  // (trigger: Custom Event `hs_lead` where Client Name contains "GA4").
  // Fully guarded so analytics can never interrupt the submit flow. We stub the
  // queue if GTM hasn't initialized yet — the container drains it once it loads.
  // The canonical option-field values + customer email are passed as event params
  // so GA4 / Enhanced Conversions can use them downstream.
  function trackGtm(payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      var email = (payload.customer && payload.customer.email) || '';

      var params = {
        event: 'hs_lead',
        form_name: 'acoustic_advice',
        email: email
      };
      var shop = payload.meta && payload.meta.shop;
      if (shop) params.store = shop;
      var country =
        payload.fields && payload.fields.country && payload.fields.country.value;
      if (country) params.country = country;

      var cf = payload.customFields || {};
      Object.keys(cf).forEach(function (name) {
        var value = cf[name] && cf[name].value;
        if (value != null && value !== '') params[name] = value;
      });

      window.dataLayer.push(params);
    } catch (err) {
      // Analytics must never interrupt the submit flow.
      if (window.console) console.warn('[acoustic-advice-form] GTM event failed', err);
    }
  }

  function setStatus(form, type, message) {
    var el = form.querySelector('[data-status]');
    if (!el) return;
    if (!type) {
      el.hidden = true;
      el.className = 'aaf__status';
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.className = 'aaf__status aaf__status--' + type;
    el.textContent = message;
  }

  function init(root) {
    var ctx = collect(root);
    if (!ctx) return;
    var form = ctx.form;
    var msg = ctx.messages;
    var submit = form.querySelector('[data-submit]');
    var submitLabel = form.querySelector('[data-submit-label]');
    var defaultLabel = submitLabel ? submitLabel.textContent : 'Submit';

    // Room dimensions: combine the three visible inputs into one submitted
    // value ("H: .., W: .., L: ..") carried by the hidden [data-dimensions-output]
    // field. The visible inputs aren't submitted individually (no data-*-field).
    var dimOutput = form.querySelector('[data-dimensions-output]');
    if (dimOutput) {
      var dimInputs = form.querySelectorAll('[data-dimension]');
      var updateDimensions = function () {
        var any = false;
        var parts = [];
        dimInputs.forEach(function (input) {
          var v = input.value.trim();
          if (v) any = true;
          parts.push(input.getAttribute('data-dim') + ': ' + v);
        });
        dimOutput.value = any ? parts.join(', ') : '';
      };
      dimInputs.forEach(function (input) {
        input.addEventListener('input', updateDimensions);
      });
    }

    // Checkbox groups requiring at least one selection: mirror `required` across
    // the group so the native reportValidity() check enforces "pick at least one".
    form.querySelectorAll('[data-checkbox-required-group]').forEach(function (group) {
      var boxes = group.querySelectorAll('input[type="checkbox"]');
      var sync = function () {
        var anyChecked = Array.prototype.some.call(boxes, function (b) { return b.checked; });
        boxes.forEach(function (b) { b.required = !anyChecked; });
      };
      boxes.forEach(function (b) { b.addEventListener('change', sync); });
      sync();
    });

    // "Something else" checkbox reveals a free-text field; clear it when hidden.
    var soundOtherToggle = form.querySelector('[data-sound-other-toggle]');
    var soundOtherField = form.querySelector('[data-sound-other-field]');
    if (soundOtherToggle && soundOtherField) {
      var soundOtherInput = soundOtherField.querySelector('input, textarea');
      var syncSoundOther = function () {
        soundOtherField.hidden = !soundOtherToggle.checked;
        if (!soundOtherToggle.checked && soundOtherInput) soundOtherInput.value = '';
      };
      soundOtherToggle.addEventListener('change', syncSoundOther);
      // form.reset() unchecks the toggle but fires no `change` event, so re-sync
      // after a reset (deferred: the `reset` event runs before values clear).
      form.addEventListener('reset', function () { setTimeout(syncSoundOther, 0); });
      syncSoundOther();
    }

    // Country: pre-select from Shopify's geoIP suggestion (window.gikGeo, set by
    // the theme-wide `ip-country-detect` snippet from /browsing_context_suggestions.json).
    // Only fills when the field is still unset and the user hasn't chosen — the
    // shown selection is what gets submitted, so no extra field is needed.
    var countrySelect = form.querySelector('select[name="country"]');
    // Rebuild options with ISO values (localized names) so the submitted value is
    // a stable code for region routing. No-op if Intl.DisplayNames is unavailable.
    populateCountryIso(countrySelect, document.documentElement.lang || 'en');
    if (countrySelect && window.gikGeo && typeof window.gikGeo.then === 'function') {
      var userPickedCountry = false;
      countrySelect.addEventListener('change', function () { userPickedCountry = true; });
      window.gikGeo.then(function (geo) {
        if (!geo || userPickedCountry || countrySelect.value) return;
        // Preferred: match the geoIP ISO against the (now ISO-coded) option values.
        var iso = (geo.iso || '').trim().toUpperCase();
        if (iso) {
          for (var i = 0; i < countrySelect.options.length; i++) {
            if (countrySelect.options[i].value.toUpperCase() === iso) {
              countrySelect.value = countrySelect.options[i].value;
              return;
            }
          }
        }
        // Fallback (server-rendered options / no Intl): match by country name.
        var name = (geo.name || '').trim().toLowerCase();
        if (!name) return;
        for (var j = 0; j < countrySelect.options.length; j++) {
          var o = countrySelect.options[j];
          if (!o.value) continue; // skip the placeholder
          if (o.value.trim().toLowerCase() === name ||
              o.textContent.trim().toLowerCase() === name) {
            countrySelect.value = o.value;
            break;
          }
        }
      }).catch(function () { /* geo unavailable — leave the placeholder */ });
    }

    // File chooser: accumulate across picks, allow per-file removal.
    var fileInput = form.querySelector('[data-files]');
    var fileList = form.querySelector('[data-file-list]');
    var selectedFiles = [];

    // Push selectedFiles back into the <input> so validation + submit see them.
    function syncInputFiles() {
      var dt = new DataTransfer();
      selectedFiles.forEach(function (f) { dt.items.add(f); });
      fileInput.files = dt.files;
    }

    function renderFileList() {
      fileList.innerHTML = '';
      fileList.hidden = selectedFiles.length === 0;
      selectedFiles.forEach(function (f, i) {
        var li = document.createElement('li');
        li.className = 'aaf__file-item';

        var name = document.createElement('span');
        name.className = 'aaf__file-name';
        name.textContent = f.name + ' (' + Math.round(f.size / 1024) + ' KB)';

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'aaf__file-remove';
        remove.setAttribute('aria-label', (msg.removeFile || 'Remove') + ' ' + f.name);
        remove.textContent = '×';
        remove.addEventListener('click', function () {
          selectedFiles.splice(i, 1);
          syncInputFiles();
          renderFileList();
          setStatus(form, '', '');
        });

        li.appendChild(name);
        li.appendChild(remove);
        fileList.appendChild(li);
      });
    }

    if (fileInput && fileList) {
      fileInput.addEventListener('change', function () {
        var prev = selectedFiles.slice();
        // Merge newly-picked files into the running set (dedupe by name+size).
        Array.prototype.forEach.call(fileInput.files, function (f) {
          var dupe = selectedFiles.some(function (x) {
            return x.name === f.name && x.size === f.size;
          });
          if (!dupe) selectedFiles.push(f);
        });

        var err = validateFiles(selectedFiles, msg);
        if (err) {
          selectedFiles = prev; // roll back this pick, keep the valid set
          setStatus(form, 'error', err);
        } else {
          setStatus(form, '', '');
        }
        syncInputFiles();
        renderFileList();
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!form.reportValidity()) return;

      var files = fileInput ? fileInput.files : [];
      var fileErr = validateFiles(files, msg);
      if (fileErr) { setStatus(form, 'error', fileErr); return; }

      var payload = buildPayload(ctx);

      if (!ctx.endpoint) {
        // No endpoint configured yet — surface the payload for wiring/QA.
        console.info('[acoustic-advice-form] no endpoint configured; payload:', payload);
        setStatus(form, 'error', 'Form endpoint is not configured yet.');
        return;
      }

      // No files -> pure JSON. Files present -> multipart/form-data with the
      // JSON in a `payload` field and each file appended (server forwards them
      // to Gorgias /api/upload, then attaches the results to the ticket).
      var fetchOpts;
      if (files.length) {
        var fd = new FormData();
        fd.append('payload', JSON.stringify(payload));
        Array.prototype.forEach.call(files, function (f) {
          fd.append('files[]', f, f.name);
        });
        fetchOpts = { method: 'POST', headers: { 'Accept': 'application/json' }, body: fd };
      } else {
        fetchOpts = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        };
      }

      submit.disabled = true;
      if (submitLabel) submitLabel.textContent = msg.submitting;

      fetch(ctx.endpoint, fetchOpts)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json().catch(function () { return {}; });
        })
        .then(function (data) {
          // Fire the attribution events on success.
          trackTripleWhale(payload);
          trackGtm(payload);

          // Notify external listeners (e.g. a GTM/Stape dataLayer bridge) of a
          // successful submit. The form stays on the page, so listeners have all
          // the time they need — no navigation to race.
          form.dispatchEvent(new CustomEvent('acoustic-advice:submitted', {
            bubbles: true,
            cancelable: false,
            detail: { payload: payload, response: data }
          }));

          form.reset();
          selectedFiles = [];
          if (fileInput) syncInputFiles();
          if (fileList) { fileList.innerHTML = ''; fileList.hidden = true; }
          setStatus(form, 'success', msg.success);
        })
        .catch(function (err) {
          console.error('[acoustic-advice-form] submit failed', err);
          setStatus(form, 'error', msg.error);
        })
        .finally(function () {
          submit.disabled = false;
          if (submitLabel) submitLabel.textContent = defaultLabel;
        });
    });
  }

  function boot() {
    document.querySelectorAll('[data-acoustic-advice-form-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
